import {type FC, type MouseEventHandler, useEffect, useRef, useState} from 'react';
import {Button} from '../atoms/Button';
import {Typography} from '../atoms/Typography';
import {useBrowserWidth} from '../hooks/useBrowserWidth';
import {DiamondIcon} from '../svg/DiamondIcon';

interface TemplateTypeIndicatorProps {
  isPrem?: boolean;
  isGc?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  dataQaValue?: string;
}

export const TemplateTypeIndicator: FC<TemplateTypeIndicatorProps> = ({
  isPrem = false,
  isGc = false,
  onClick,
  dataQaValue,
}) => {
  const type = () => {
    if (isGc) return 'Premium Greeting Card';
    return isPrem ? 'Premium Invitation' : 'Free Invitation';
  };

  const classModifier = () => {
    if (isGc) return '--gc';
    return isPrem ? '--premium' : '';
  };

  const browserWidth = useBrowserWidth();
  const rolloverRef = useRef(null);
  const [typeIndicatorHeight, setTypeIndicatorHeight] = useState<string>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger when browser width changes
  useEffect(() => {
    const indicator = document.querySelector('#template-type-indicator-first');
    if (!indicator) return;
    setTypeIndicatorHeight(`${indicator.clientHeight}px`);
  }, [browserWidth]);

  return !onClick || isGc || !isPrem ? (
    <div className={`template-indicator${classModifier()} template-indicator--static`}>
      {isPrem && <DiamondIcon className="template-indicator-icon" />}
      <Typography variant="paragraph3" className="mobile-paragraph3">
        {type()}
      </Typography>
    </div>
  ) : (
    <Button
      className="template-indicator--button"
      data-qa-id="premium-invitation-indicator"
      data-qa-value={dataQaValue}
      variant="transparent"
      onClick={onClick}
      // Without these two props, TypeScript complains about missing props
      // When Button.jsx is converted to TypeScript, these props can be removed or improved
      ariaLabel=""
      id=""
    >
      <DiamondIcon className="template-indicator-icon" />
      <div
        className="template-indicator__with-rollover"
        ref={rolloverRef}
        style={{height: typeIndicatorHeight}}
      >
        <Typography
          className={`template-indicator${classModifier()}`}
          id="template-type-indicator-first"
          variant="paragraph3"
        >
          {type()}
        </Typography>
      </div>
    </Button>
  );
};
